<template>
  <v-container :fluid="$vuetify.breakpoint.lgAndDown">
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            <div>Validations</div>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Rechercher..."
              single-line
              hide-details
              outlined
              dense
            ></v-text-field>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :loading="loading"
            :items="validations"
            :server-items-length="total"
            :search="search"
            class="elevation-1"
            :options.sync="options"
            :footer-props="{
              itemsPerPageOptions: [10, 30, 50],
              itemsPerPageText: 'Élements par page',
            }"
            @pagination="paginationUpdate"
          >
            <!-- eslint-disable-next-line vue/valid-v-slot justification: possible according to vuetify documentation -->
            <template
              v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }"
              >{{ pageStart }}-{{ pageStop }} de {{ itemsLength }}</template
            >
            <template v-slot:body="{ items }">
              <tbody class="text-left cursor-pointer">
                <tr
                  v-for="item in items"
                  :key="item._id"
                  @click="goToValidation(item)"
                >
                  <td
                    :class="
                      item.deletedAt ? 'text-decoration-line-through' : ''
                    "
                  >
                    E-{{ item.orderId }}-{{ item.creatorTla }}
                  </td>
                  <td
                    :class="
                      item.deletedAt ? 'text-decoration-line-through' : ''
                    "
                  >
                    {{ item.supplier.name }}
                  </td>
                  <td
                    :class="
                      item.deletedAt ? 'text-decoration-line-through' : ''
                    "
                  >
                    {{
                      new Intl.NumberFormat("fr-CH", {
                        style: "currency",
                        currency: "CHF",
                      }).format(item.computedTotal)
                    }}
                  </td>
                  <td
                    :class="
                      item.deletedAt ? 'text-decoration-line-through' : ''
                    "
                  >
                    {{ item.year }}
                  </td>
                  <td
                    :class="
                      item.deletedAt ? 'text-decoration-line-through' : ''
                    "
                  >
                    {{ format(parseISO(item.poDate), "dd.MM.yyyy") }}
                  </td>
                </tr>
              </tbody></template
            >
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" top right>
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">
          Fermer
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import Intl from "intl";
import { format, parseISO } from "date-fns";

export default {
  async mounted() {
    this.loadFromURL();
    await this.getDataFromApi();
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },
  props: ["setBadge"],
  data: () => ({
    isInitialLoad: true,
    snackbar: {
      visible: false,
      color: "",
      text: "",
    },
    validations: undefined,
    search: "",
    loading: false,
    deleteConfirmation: "",
    total: 0,
    deletedAt: false,
    options: {},
    headers: [
      {
        text: "N° BC",
        align: "start",
        value: "orderId",
      },
      { text: "Fournisseur", value: "supplier.name" },
      { text: "Montant", value: "computedTotal" },
      { text: "Année", value: "year" },
      { text: "Date de création", value: "poDate" },
    ],
    Intl,
    format,
    parseISO,
  }),
  methods: {
    loadFromURL() {
      // Load search from URL
      if (this.$route.query.search) {
        this.search = this.$route.query.search;
      }

      // Load pagination options from URL
      if (this.$route.query.page) {
        this.options.page = parseInt(this.$route.query.page);
      }
      if (this.$route.query.itemsPerPage) {
        this.options.itemsPerPage = parseInt(this.$route.query.itemsPerPage);
      }
      if (this.$route.query.sortBy) {
        this.options.sortBy = this.$route.query.sortBy.split(',');
      }
      if (this.$route.query.sortDesc) {
        this.options.sortDesc = this.$route.query.sortDesc.split(',').map(d => d === 'true');
      }
    },

    updateURL: debounce(async function() {
      const query = { ...this.$route.query };

      // Update search in URL
      if (this.search && this.search.trim()) {
        query.search = this.search.trim();
      } else {
        delete query.search;
      }

      // Update pagination in URL
      if (this.options.page && this.options.page > 1) {
        query.page = this.options.page;
      } else {
        delete query.page;
      }

      if (this.options.itemsPerPage && this.options.itemsPerPage !== 10) {
        query.itemsPerPage = this.options.itemsPerPage;
      } else {
        delete query.itemsPerPage;
      }

      if (this.options.sortBy && this.options.sortBy.length > 0) {
        query.sortBy = this.options.sortBy.join(',');
      } else {
        delete query.sortBy;
      }

      if (this.options.sortDesc && this.options.sortDesc.length > 0) {
        query.sortDesc = this.options.sortDesc.map(d => d.toString()).join(',');
      } else {
        delete query.sortDesc;
      }

      // Update URL without triggering navigation
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        try {
          await this.$router.replace({ query });
        } catch (err) {
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        }
      }
    }, 300),

    paginationUpdate(value) {
      if (this.$route.query && this.$route.query.status) {
        if (this.$route.query.status === "processing") {
          if (this.$route.query.type === "notice") {
            this.setBadge("pendingNotices", value.itemsLength);
          } else {
            this.setBadge("pendingValidations", value.itemsLength);
          }
        } else {
          if (this.$route.query.status === "to-update") {
            this.setBadge("pendingUpdates", value.itemsLength);
          }
        }
      }
    },
    async getDataFromApi() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      try {
        const { data } = await axios({
          method: "get",
          url: `${process.env.VUE_APP_API_URI}/validations?status=${this.$route.query.status}&type=${this.$route.query.type}&search=${this.search}&page=${page}&sortBy=${sortBy}&sortDesc=${sortDesc}&itemsPerPage=${itemsPerPage}`,
        });
        let { docs: items } = data;
        const total = data.totalDocs;
        this.total = total;
        this.validations = items;
        this.updateURL();
      } catch (err) {
        if (err.message !== 'Network Error') {
          console.error('Failed to fetch validations:', err.message);
        }
      } finally {
        this.loading = false;
      }
    },

    goToValidation(item) {
      // Build query params to preserve current search context
      const contextQuery = {};

      // Preserve search context from current route
      if (this.$route.query.search) {
        contextQuery.fromSearch = this.$route.query.search;
      }
      if (this.$route.query.page) {
        contextQuery.fromPage = this.$route.query.page;
      }
      if (this.$route.query.itemsPerPage) {
        contextQuery.fromItemsPerPage = this.$route.query.itemsPerPage;
      }
      if (this.$route.query.sortBy) {
        contextQuery.fromSortBy = this.$route.query.sortBy;
      }
      if (this.$route.query.sortDesc) {
        contextQuery.fromSortDesc = this.$route.query.sortDesc;
      }

      // Add current list context
      contextQuery.fromList = 'validations';
      contextQuery.fromStatus = this.$route.query.status;
      if (this.$route.query.type) {
        contextQuery.fromType = this.$route.query.type;
      }

      this.$router.push({
        path: `/orders/${item._id}/validations`,
        query: contextQuery
      });
    },
  },
  watch: {
    options: {
      handler: debounce(async function() {
        if (this.isInitialLoad) return;
        await this.getDataFromApi();
      }, 300),
      deep: true,
    },
    "$route.query.type": debounce(async function() {
      if (this.isInitialLoad) return;
      await this.getDataFromApi();
    }, 300),
    "$route.query.status": debounce(async function() {
      if (this.isInitialLoad) return;
      await this.getDataFromApi();
    }, 300),
    search: debounce(async function () {
      if (this.isInitialLoad) return;
      if (this.options.page === 1) {
        await this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    }, 500),
  },
};
</script>